import React, { createContext, useState, useContext } from 'react';
import { api } from 'api';
import { postDataWithSignature } from './utils';

export type AuthUser = api.Account & { userId: string };
interface AuthContextProps {
  user: AuthUser | null;
  login: (userData: api.Account) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      // Проверяем, есть ли userId в сохранённых данных
      return parsedUser.userId ? parsedUser : null;
    }
    return null;
  });

  const fetchInternalId = async (publicId: string): Promise<string> => {
    return postDataWithSignature(publicId);
  };

  const login = async (userData: api.Account) => {
    const basicUserInfo = {
      publicId: userData.publicId,
      name: userData.name,
      avatarId: userData.avatarId,
    };

    try {
      const internalId = await fetchInternalId(userData.publicId);

      const userWithInternalId = {
        ...basicUserInfo,
        userId: internalId, // Предполагаем, что ответ содержит userId
      };
      setUser({ ...userData, userId: internalId });
      localStorage.setItem('user', JSON.stringify(userWithInternalId));
    } catch (error) {
      console.error('Ошибка при получении внутреннего ID:', error);
      // Обработка ошибок, возможно установка состояния в null или показ сообщения пользователю
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const contextValue = {
    user,
    login,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
